import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MusicalNoteIcon, BellSlashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface VolumeSliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export default function VolumeSlider({ value, onChange, disabled = false }: VolumeSliderProps) {
  const { t } = useTranslation('editor');
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        const sliderElement = document.getElementById('volume-slider-popup');
        if (sliderElement && !sliderElement.contains(event.target as Node)) {
          setIsOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8,
        left: rect.left + rect.width / 2
      });
    }
  }, [isOpen]);

  const isMuted = value === 0;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="transition-colors flex items-center justify-center flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
        title={t('common.volume')}
      >
        {isMuted ? (
          <BellSlashIcon className="w-3 h-3 text-red-500" />
        ) : (
          <MusicalNoteIcon className="w-3 h-3 text-green-500" />
        )}
      </button>

      {isOpen && createPortal(
        <div
          id="volume-slider-popup"
          className="fixed bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-[9999] p-3"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-500 mb-1">100%</span>
            <div className="relative h-32 w-6 flex items-center justify-center">
              <div className="absolute h-full w-1.5 bg-gray-700 rounded-full" />
              <div
                className="absolute bottom-0 w-1.5 bg-blue-500 rounded-full transition-all"
                style={{ height: `${value * 100}%` }}
              />
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                disabled={disabled}
                className="absolute h-full w-full opacity-0 cursor-pointer"
                style={{
                  writingMode: 'vertical-lr',
                  direction: 'rtl'
                }}
              />
              <div
                className="absolute w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-md pointer-events-none"
                style={{
                  bottom: `calc(${value * 100}% - 8px)`
                }}
              />
            </div>
            <span className="text-xs text-gray-500 mt-1">0%</span>
          </div>
          <div className="text-center mt-2">
            <span className="text-sm text-blue-400 font-medium">{Math.round(value * 100)}%</span>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
